import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { orderService } from '../../services/orderService';
import { productService } from '../../services/productService';
import { customerService } from '../../services/customerService';
import { stockService } from '../../services/stockService';
import { OrderSummary, Product, Order, Stock } from '../../types';

// Define the dashboard data interface
interface DashboardData {
  orderSummary: OrderSummary | null;
  totalProducts: number;
  totalCustomers: number;
  lowStockProducts: Product[];
  recentOrders: Order[];
  stockAlerts: Stock[];
}

// Define the initial state interface
interface DashboardState {
  data: DashboardData;
  isLoading: boolean;
  error: string | null;
  lastRefresh: string | null;
}

// Define the initial state
const initialState: DashboardState = {
  data: {
    orderSummary: null,
    totalProducts: 0,
    totalCustomers: 0,
    lowStockProducts: [],
    recentOrders: [],
    stockAlerts: [],
  },
  isLoading: false,
  error: null,
  lastRefresh: null,
};

// Async thunk to fetch all dashboard data
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch all dashboard data in parallel
      const [
        orderSummary,
        products,
        customers,
        recentOrdersResponse,
        lowStockItems,
      ] = await Promise.allSettled([
        orderService.getOrderSummary(),
        productService.getProducts({ limit: 1000 }),
        customerService.getCustomers({ limit: 1000 }),
        orderService.getOrders({ limit: 10 }),
        stockService.getLowStockReport(),
      ]);

      // Process results with fallbacks for failed requests
      const dashboardData: DashboardData = {
        orderSummary: orderSummary.status === 'fulfilled' ? orderSummary.value : null,
        totalProducts: products.status === 'fulfilled' ? (products.value as any).total || (products.value as any).data?.length || 0 : 0,
        totalCustomers: customers.status === 'fulfilled' ? (customers.value as any).total || (customers.value as any).data?.length || 0 : 0,
        recentOrders: recentOrdersResponse.status === 'fulfilled' ? (recentOrdersResponse.value as any).items || [] : [],
        lowStockProducts: [], // Will be populated from stock data
        stockAlerts: lowStockItems.status === 'fulfilled' ? lowStockItems.value.map(item => ({
          stock_id: item.product_id,
          product_id: item.product_id,
          available_quantity: item.available_quantity,
          reserved_quantity: item.reserved_quantity,
          reorder_level: item.min_stock_level,
          max_stock_level: item.max_stock_level,
          location: '',
          is_low_stock: item.is_low_stock,
          create_time: new Date().toISOString(),
          update_time: new Date().toISOString(),
        })) : [],
      };

      return dashboardData;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch dashboard data');
    }
  }
);

// Async thunk to refresh order summary only
export const refreshOrderSummary = createAsyncThunk(
  'dashboard/refreshOrderSummary',
  async (_, { rejectWithValue }) => {
    try {
      const orderSummary = await orderService.getOrderSummary();
      return orderSummary;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh order summary');
    }
  }
);

// Async thunk to refresh recent orders only
export const refreshRecentOrders = createAsyncThunk(
  'dashboard/refreshRecentOrders',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrders({ limit });
      return response.items;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh recent orders');
    }
  }
);

// Create the dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateLastRefresh: (state) => {
      state.lastRefresh = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.lastRefresh = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Refresh order summary
      .addCase(refreshOrderSummary.fulfilled, (state, action) => {
        state.data.orderSummary = action.payload;
        state.lastRefresh = new Date().toISOString();
      })
      .addCase(refreshOrderSummary.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Refresh recent orders
      .addCase(refreshRecentOrders.fulfilled, (state, action) => {
        state.data.recentOrders = action.payload;
        state.lastRefresh = new Date().toISOString();
      })
      .addCase(refreshRecentOrders.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { clearError, updateLastRefresh } = dashboardSlice.actions;

// Export selectors (using a generic state type to avoid circular imports)
export const selectDashboardData = (state: any) => state.dashboard.data;
export const selectDashboardLoading = (state: any) => state.dashboard.isLoading;
export const selectDashboardError = (state: any) => state.dashboard.error;
export const selectDashboardLastRefresh = (state: any) => state.dashboard.lastRefresh;
export const selectOrderSummary = (state: any) => state.dashboard.data.orderSummary;
export const selectRecentOrders = (state: any) => state.dashboard.data.recentOrders;
export const selectStockAlerts = (state: any) => state.dashboard.data.stockAlerts;
export const selectTotalProducts = (state: any) => state.dashboard.data.totalProducts;
export const selectTotalCustomers = (state: any) => state.dashboard.data.totalCustomers;

// Export reducer
export default dashboardSlice.reducer;
